import React, { useState } from 'react';
import Link from 'next/link';
import IconButton from '@mui/material/IconButton';

import Search from '@mui/icons-material/Search';
import NavList from './NavList';
import NavMenu from './NavMenu';
import SearchBox from './SearchBox';
import UserButtons from '../UserButtons';

import styles from './nav-bar.module.scss';
import Image from 'next/image';
import { useUser } from '@/utils/supabase/useUser';
import UserMenu from '../Header/UserMenu';
import MobileMenu from './MobileMenu';
import { PopupTypes, usePopups } from '@/providers/popups';

type NavBarProps = {
  position: string;
  fixed: boolean;
  articleName: string;
  slug: string;
  contentType: TUDContentType;
  isAuthenticated: boolean;
  isProfessionalMember: boolean;
  sectionBoxOpen: boolean;
  setSectionBoxOpen: (open: boolean) => void;
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
  menu: Menu | null;
  showWelcomeHighlight?: boolean;
};
const largeLogo = '/static/imgs/logo/tud-logo-horizontal.png';
const smallLogo = '/static/imgs/logo/tud-logo-graphic.png';

const NavBar = ({
  position,
  fixed,
  isAuthenticated,
  isProfessionalMember,
  sectionBoxOpen,
  setSectionBoxOpen,
  searchOpen,
  setSearchOpen,
  menu,
  showWelcomeHighlight
}: NavBarProps) => {
  const { user, signOut } = useUser();
  const { openPopup } = usePopups();

  const [buttonsOpen, setButtonsOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const userMenuOpen = Boolean(anchorEl);
  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleSearchBoxOpen = () => {
    if (sectionBoxOpen) {
      setSectionBoxOpen(false);
    }
    setSearchOpen(!searchOpen);
  };

  return (
    <>
      <div
        className={`nav-bar nav-bar--${position}${fixed ? ' nav-bar--fixed' : ''}`}
      >
        <div className='nav-bar__inner'>
          <div className='container'>
            <div className={`nav-bar--fixed__main ${styles.navBarFixedWrap}`}>
              <Link
                prefetch={false}
                href='/'
                className='nav-bar--fixed__logo-box'
              >
                <div
                  className='large-logo-img'
                  style={{
                    backgroundImage: `url(${largeLogo})`,
                    backgroundSize: 'contain'
                  }}
                />
                <Image
                  className='small-logo-img'
                  src={smallLogo}
                  alt='The Urban Developer'
                  width={45}
                  height={45}
                  priority={true}
                />
              </Link>
              {buttonsOpen && (
                <div className={`user-btn-group ${styles.userBtnsWrap}`}>
                  <UserButtons
                    isAuthenticated={isAuthenticated}
                    userMenuOpen={userMenuOpen}
                    handleUserMenuOpen={handleUserMenuClick}
                    showWelcomeHighlight={showWelcomeHighlight}
                  />
                </div>
              )}
              <UserMenu
                id='user-menu'
                anchorEl={anchorEl}
                userMenuOpen={userMenuOpen}
                handleUserMenuClose={handleUserMenuClose}
                handleLogout={signOut}
                showProfessionalLinks={isProfessionalMember}
                showWelcomeHighlight={showWelcomeHighlight}
              />
            </div>
          </div>
          <div className='nav-bar__row'>
            <div className='container'>
              <div className='nav-bar__main'>
                <Link prefetch={false} href='/latest' className='nav-link'>
                  <div className='nav-link__text'>
                    Latest
                    <div className='nav-link__underline' />
                  </div>
                </Link>
                <MobileMenu
                  menu={menu}
                  isAuthenticated={isAuthenticated}
                  isProfessionalMember={isProfessionalMember}
                />
                <div className='nav-menu-wrapper'>
                  {NavList.map((nav) => {
                    const hasFieldOrChild =
                      nav.field ?? nav.child ?? nav.fields;

                    if (hasFieldOrChild) {
                      const fieldName = nav.field as MenuKey;
                      return (
                        <NavMenu
                          key={nav.name}
                          child={nav.child}
                          name={nav.name}
                          isIcon={false}
                          menuItems={menu ?? undefined}
                          menuKey={fieldName}
                          menuKeys={nav.fields as unknown as MenuKey[]}
                        />
                      );
                    }
                    return (
                      <Link
                        prefetch={false}
                        key={nav.name}
                        href='#'
                        className='nav-link'
                      >
                        <div className='nav-link__text'>
                          {nav.name}
                          <div className='nav-link__underline' />
                        </div>
                      </Link>
                    );
                  })}
                </div>
                <Link
                  prefetch={false}
                  onClick={
                    user
                      ? undefined
                      : () => openPopup(PopupTypes.NewsletterSubscription)
                  }
                  href={user ? '/my-account/email-preferences' : '#'}
                  className='nav-link'
                >
                  <div className='nav-link__text'>
                    Newsletters
                    <div className='nav-link__underline' />
                  </div>
                </Link>
                <div className={`nav-link ${styles.searchBtn}`}>
                  <IconButton
                    onClick={toggleSearchBoxOpen}
                    aria-label='Search'
                    size='large'
                  >
                    <Search style={{ fill: '#58595B' }} />
                  </IconButton>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* if need, import UrbanityBanner and uncoment line below to show it */}
        {/* {fixed && !isMobile && <UrbanityBanner />} */}
        <SearchBox
          open={searchOpen}
          toggleSearchBoxOpen={toggleSearchBoxOpen}
        />
      </div>
    </>
  );
};

export default NavBar;
